import React, { forwardRef, useLayoutEffect, useRef } from "react";
import Data from "../utils/info";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import SplitText from "gsap/SplitText";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(SplitText, ScrollTrigger, useGSAP);

const getHeartScale = (
	t,
	A = 0.08, // Scale amplitude			0.2 (±20%
	f = 0.48, // Frequency (Hz) 1.2hz		72BPM 72/60
	k1 = 40, // Sharpness of 1st beat		20–40
	k2 = 10, // Sharpness of 2nd beat		10–30
	phi1 = 0, // Phase of first beat		0
	phi2 = Math.PI * 0.35, // Phase of second beat	π/2, π * 0.6, etc.
) => {
	const s1 = Math.sin(2 * Math.PI * f * t - phi1);
	const s2 = Math.sin(2 * Math.PI * f * t - phi2);
	// console.log(`s1:${s1}, s2:${s2}`);

	const bump1 = Math.exp(-k1 * s1 * s1);
	const bump2 = Math.exp(-k2 * s2 * s2);
	// console.log(`bump1:${bump1}, bump2:${bump2}`);

	// 0.9 means animation starts from 90% of original size
	return 1 + A * (bump1 + bump2);
};

const Home = forwardRef((props, ref) => {
	const { title } = Data[0].home;
	const rafId = useRef(null);
	const startTimeRef = useRef(null);
	const heartWordRef = useRef(null);
	const allLinesRef = useRef(null);
	const allWordsRef = useRef(null);
	const splitRef = useRef(null);

	useLayoutEffect(() => {
		const wordHeart = document.querySelector("#heart");
		let resizeRafId = null;

		const init = () => {
			// reset/kill prevoius splits text references
			if (splitRef.current) splitRef.current.revert();
			// re-split after resize ----1
			splitRef.current = SplitText.create(wordHeart, {
				type: "words, lines",
				wordsClass: "words",
				linesClass: "lines",
				autoSplit: true,
			});
			// Find the 🫀 word once & update the word ref
			heartWordRef.current = splitRef.current.words.find(
				(wd) => wd.innerHTML === "🫀",
			);
			if (heartWordRef.current) {
				heartWordRef.current.style.willChange = "transform";
			}

			allLinesRef.current = splitRef.current.lines;
			allWordsRef.current = splitRef.current.words;
			gsap.from(allLinesRef.current, {
				yPercent: 50,
				rotateZ: 3,
				scale: 0.96,
				opacity: 0,
				delay: 0.2,
				duration: 0.6,
				stagger: 0.1,
				marker: true,
				ease: "back.inOut",
			});
			gsap.from(allWordsRef.current, {
				yPercent: 50,
				opacity: 0,
				rotateZ: 5,
				duration: 1,
				ease: "back.inOut",
			});
		};

		const anim = (time) => {
			if (!startTimeRef.current) startTimeRef.current = time;
			const elapsedTime = (time - startTimeRef.current) / 1000;

			const el = heartWordRef.current;
			if (el && document.body.contains(el)) {
				const scale = getHeartScale(elapsedTime);
				// console.log(
				// 	`element is:${el.classList} && ${document.body.contains(el)}`
				// );

				el.style.transform = `scale(${scale})`;
			}
			// else {
			// console.log(
			// 	`element not found:${el.classList} && ${document.body.contains(
			// 		el
			// 	)}`
			// );
			// }

			rafId.current = requestAnimationFrame(anim);
		};

		// start animation
		init();
		rafId.current = requestAnimationFrame(anim);

		const scheduleInit = () => {
			if (resizeRafId) cancelAnimationFrame(resizeRafId);
			resizeRafId = requestAnimationFrame(() => {
				init();
				resizeRafId = null;
			});
		};

		// re-initialize on resize
		const ro = new ResizeObserver(scheduleInit);
		ro.observe(wordHeart);

		// re init after resize --- refer to ----1
		const HandleResize = () => scheduleInit();
		window.addEventListener("resize", HandleResize);
		// re-init on ScrollTrigger refresh (in case GSAP flushes DOM)
		const refreshTrigger = ScrollTrigger.create({
			trigger: ".home",
			start: "top bottom",
			onEnterBack: () => scheduleInit(),
		});

		return () => {
			// cleanup
			cancelAnimationFrame(rafId.current);
			if (resizeRafId) cancelAnimationFrame(resizeRafId);
			ro.disconnect();
			window.removeEventListener("resize", HandleResize);
			refreshTrigger.kill();
			if (splitRef.current) splitRef.current.revert();
		};
	}, []);

	return (
		<section ref={ref} data-section="home" className="home_container snapper">
			<div className="home">
				<h1 className="title" id="heart">
					{title}
				</h1>
			</div>
		</section>
	);
});

export default Home;
